
import { UPDATE_CUSTOM_FIELD_OCR_IMAGE_RESULT } from '@/store/actions'
import { getChildrenValue, uploadImage } from '@/utils/api'
import { DATE_TIME, INPUT_COMPONENTS } from '@/utils/constants'
import errorHandler from '@/utils/errorHandler'
import { dataURLtoFile, deepSearch } from '@/utils/helpers'
import { format, isValid, parse } from 'date-fns'
import { UploadFile } from 'element-plus/lib/el-upload/src/upload.type'
import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqual'
import { composeFieldValue, hasActiveAutoIncrement, isAutoIncrementPart } from 'smartbarcode-web-core/src/bo/composeField'
import { parseExternalId, TParsableField } from 'smartbarcode-web-core/src/bo/externalId'
import { eligibleFields } from 'smartbarcode-web-core/src/bo/fieldEligibility'
import { maskPhoneNum } from 'smartbarcode-web-core/src/utils/barcodeHelper'
import { ECustomFieldType } from 'smartbarcode-web-core/src/utils/enums/index'
import getGhgEmissionMap from 'smartbarcode-web-core/src/utils/ghgEmission/calc'
import { GhgEmissionCalculationMaterial } from 'smartbarcode-web-core/src/utils/ghgEmission/GhgEmissionCalculationMaterial'
import { GhgEmissionCustomFieldConclusion } from 'smartbarcode-web-core/src/utils/ghgEmission/GhgEmissionCustomFieldConclusion'
import { GhgEmissionInput } from 'smartbarcode-web-core/src/utils/ghgEmission/GhgEmissionInput'
import { isEmpty } from 'smartbarcode-web-core/src/utils/typeChecker'
import {
  IActivationData,
  ICustomFieldData,
  ICustomFormFields,
  IFieldSettings,
  IFormInputSchema,
  IListObjectItem,
  IProjectCustomField,
  TFieldNotiSettings,
  TFormSchema,
  TOptions,
} from 'smartbarcode-web-core/src/utils/types/index'
import { Options, Vue } from 'vue-class-component'
import { Watch } from 'vue-property-decorator'

@Options({
  components: {},
  name: 'CustomFieldsMixin',
})
export default class CustomFieldsMixin extends Vue {
  formModel: Record<string, unknown> = {}

  // Return the raws files if any file uploades
  fileListFormModel: Record<string, UploadFile[]> = {}

  /**
   * Override this method for insert data
   */
  get customForm(): Record<string, IFieldSettings | TFieldNotiSettings> {
    return {}
  }

  /**
   * Flatten fields of form schema
   */
  get flattenFields(): Record<string, IFieldSettings> {
    const fields = this.customForm
    const flatten = Object.keys(fields).reduce((acc, cur) => {
      // exclude nextTrackPointNotification now
      if (cur === 'nextTrackPointNotification') return acc

      if (cur === ECustomFieldType.LOCATION) {
        return {
          ...acc,
          [cur]: {
            order: 0, // Hardcode order of location data to the top
            fieldType: ECustomFieldType.LOCATION,
            ...fields[cur],
          },
        }
      }
      return { ...acc, ...fields[cur] }
    }, {})
    return flatten
  }

  /**
   * Select form schema
   */
  get selectedFormSchema() {
    const sortedFields = Object.entries(this.flattenFields)
      .sort((a, b) => {
        const orderA = a[1].order ?? 999
        const orderB = b[1].order ?? 999
        return orderA - orderB
      })
      .filter((f) => {
        // Only fieldType is Location need to be check on `isAvailable` fields.
        // This is inconsistent of field schema
        if (f?.[1]?.fieldType === ECustomFieldType.LOCATION) {
          return f[1].isAvailable
        }
        return !f[1].isHidden
      })

    const customFormSchema: TFormSchema = {}
    sortedFields.forEach(([fieldName, inputSchema]) => {
      const { fieldType, multiLine, maxLength, minLength, countryCode, ...others } = inputSchema

      customFormSchema[fieldName] = {
        fieldType,
        ...(fieldType && !!INPUT_COMPONENTS[fieldType] && { component: INPUT_COMPONENTS[fieldType] }),
        ...(maxLength && { maxLength }),
        ...(minLength && { minLength }),
        ...(countryCode && { countryCode }),
        ...(multiLine && { multiLine }),
        ...(fieldType === ECustomFieldType.ESIGN && { disableError: true }),
        ...others,
        ...(inputSchema.isReadOnly && { disabled: true }),
      }

      // BaseInput only forwards props it declares with @Prop (Pitfall 3), so
      // InputListObject cannot receive isComposerEnabled as a sibling prop from the
      // view — it has to travel inside this schema entry instead.
      if (fieldType === ECustomFieldType.LIST_OBJECT) {
        ;(customFormSchema[fieldName] as unknown as Record<string, unknown>).isComposerEnabled = this.isComposerEnabled
      }

      // Auto-increment never changes a field's own behaviour: input, isMandatory and
      // validation stay exactly as configured; the server appends the number at submit.
      // The one exception: a composer made of auto rows only has no input source, so
      // with every row disabled nothing can ever give the field a value — drop it.
      // Reads customFormSchema[fieldName].composer (typed IFormInputSchema, has `composer`),
      // not inputSchema.composer (typed IFieldSettings, no `composer` — strict TS would fail).
      // isComposerEnabled gates it: the edit screen shows the stored composed value as-is.
      if (fieldType === ECustomFieldType.TEXT && this.isComposerEnabled) {
        const composer = customFormSchema[fieldName].composer
        const parts = composer?.parts ?? []
        if (parts.length > 0 && parts.every(isAutoIncrementPart) && !hasActiveAutoIncrement(composer)) {
          delete customFormSchema[fieldName]
        }
      }
    })
    return customFormSchema
  }

  /**
   * Generate Custom Data for request
   */
  /**
   * Views that can reopen an already-saved record override this and return false —
   * the stored value is already composed and must not be composed a second time.
   * A trackpoint event is always new, so the default stays true.
   */
  get isComposerEnabled(): boolean {
    return true
  }

  /**
   * Applies every configured composer to the form values. The field the user typed
   * into supplies the self part; the rest come from sibling fields in the same scope.
   * Runs at submit time only — nothing is written back into the inputs.
   */
  applyFieldComposers(values: Record<string, unknown>): Record<string, unknown> {
    if (!this.isComposerEnabled) return values

    // Same gate the split side uses: only a text field owns either setting.
    // A field with an auto part is excluded so its raw value (or absent key) reaches
    // generateCustomDataForRequest untouched and the server composes + issues the number.
    const owners = Object.entries(this.selectedFormSchema).filter(
      ([, field]) =>
        field.fieldType === ECustomFieldType.TEXT && field.composer?.parts?.length && !hasActiveAutoIncrement(field.composer)
    )
    if (!owners.length) return values

    const byPath: Record<string, string | number | undefined | null> = {}
    Object.entries(values).forEach(([key, value]) => {
      byPath[`customFields.${key}`] = this.composerSourceValue(key, value)
    })

    const result = { ...values }
    owners.forEach(([key, field]) => {
      if (!field.composer) return
      const own = typeof values[key] === 'string' || typeof values[key] === 'number' ? String(values[key]) : ''
      // Only text fields and the external ID carry a composer, so the joined result
      // is always a string and needs no coercion.
      result[key] = composeFieldValue(own, field.composer, byPath)
    })
    return result
  }

  /**
   * The value a field contributes to a join. A single-select stores its selection key,
   * so the key is swapped for the label the user actually picked — the same label the
   * split direction reads back. Anything that is not a scalar cannot be joined.
   */
  composerSourceValue(fieldKey: string, value: unknown): string | number | undefined {
    const field = this.selectedFormSchema[fieldKey]
    if (field?.fieldType === ECustomFieldType.SINGLE_SELECT) {
      const selections = (field.selections as unknown) as TOptions
      return selections?.[value as string]?.label
    }
    return typeof value === 'string' || typeof value === 'number' ? value : undefined
  }

  /** The last value each split source was parsed from, so the same one is not re-split. */
  lastParsedSources: Record<string, string> = {}

  /**
   * Composes ListObject sub-fields whose composer has no auto part — once, at request-build
   * time, on a copy of each row. Rows in the form model stay raw, so re-editing an element
   * can never compose on top of an already composed value (CR-01); the level-1 rule
   * "nothing is written back into the inputs" applied per row. A sub-field with an active
   * auto part keeps its raw value: the server composes + issues the number (D-08-07).
   */
  composeListObjectRows(key: string, rows: IListObjectItem[]): IListObjectItem[] {
    if (!this.isComposerEnabled || !rows?.length) return rows

    const subFields = (((this.selectedFormSchema[key] as unknown) as Record<string, unknown>)?.subFields ??
      {}) as Record<string, IProjectCustomField>
    const owners = Object.keys(subFields).filter((code) => {
      const field = subFields[code]
      return (
        field?.fieldType === ECustomFieldType.TEXT && field.composer?.parts?.length && !hasActiveAutoIncrement(field.composer)
      )
    })
    if (!owners.length) return rows

    return rows.map((row) => {
      const customFields = { ...(row?.customFields ?? {}) }
      const byPath: Record<string, string | number | undefined | null> = {}
      Object.keys(subFields).forEach((code) => {
        byPath[`customFields.${code}`] = this.listObjectRowSourceValue(customFields[code])
      })
      owners.forEach((code) => {
        const composer = subFields[code]?.composer
        if (!composer) return
        const ownRaw = this.listObjectRowSourceValue(customFields[code])
        const composed = composeFieldValue(ownRaw !== undefined && ownRaw !== null ? String(ownRaw) : '', composer, byPath)
        // An untouched owner with nothing to join stays absent instead of gaining text: ''.
        if (composed !== '' || customFields[code]) {
          customFields[code] = { fieldType: ECustomFieldType.TEXT, text: composed } as ICustomFieldData
        }
      })
      return { ...row, customFields }
    })
  }

  /**
   * The value a typed row entry contributes to a join — single-select gives its label,
   * matching both the level-1 client rule (composerSourceValue) and the server's
   * SingleSelectFieldData.ToString().
   */
  listObjectRowSourceValue(data?: ICustomFieldData): string | number | undefined {
    if (!data) return undefined
    switch (data.fieldType) {
      case ECustomFieldType.TEXT:
        return data.text
      case ECustomFieldType.NUMBER:
        return data.number
      case ECustomFieldType.EMAIL:
        return data.email
      case ECustomFieldType.DATE:
        return data.date
      case ECustomFieldType.DATE_TIME_DATA:
        return data.dateTimeData
      case ECustomFieldType.PHONE_NUMBER:
        return data.phoneNumber?.number
      case ECustomFieldType.SINGLE_SELECT:
        return data.singleSelect?.value
      default:
        return undefined
    }
  }

  /**
   * Split: a text field carrying parsers writes into the fields it targets as the user
   * types. The watcher sees every form change, but a field is only re-split when its own
   * value changed — otherwise editing any field would keep overwriting a target the user
   * had just corrected by hand. Config validation rejects dependency cycles, which is what
   * keeps the writes it does make bounded.
   */
  @Watch('formModel', { deep: true })
  applyFieldParsers() {
    Object.entries(this.selectedFormSchema)
      .filter(([, field]) => field.fieldType === ECustomFieldType.TEXT && field.parsers?.length)
      .forEach(([key, field]) => {
        const source = this.formModel[key]
        if (typeof source !== 'string' || source === '') {
          delete this.lastParsedSources[key]
          return
        }
        if (this.lastParsedSources[key] === source) return

        const wanted = field.parsers?.map((parser) => parser.targetFieldPath) ?? []
        const targets = eligibleFields(
          Object.entries(this.selectedFormSchema)
            .map(([targetKey, target]) => ({
              key: `customFields.${targetKey}`,
              label: target.label ?? '',
              type: target.fieldType as ECustomFieldType,
              selections: target.selections ?? {},
            }))
            .filter((target) => wanted.includes(target.key)),
          'split'
        ) as TParsableField[]
        if (!targets.length) return

        // Cached only once there is something to write, so a config that is momentarily
        // targetless is retried rather than skipped for good. Set before the parse so a
        // rule that throws is not re-run on every later keystroke.
        this.lastParsedSources[key] = source
        try {
          Object.entries(parseExternalId(source, targets, field.parsers ?? [])).forEach(([path, value]) => {
            const targetKey = path.replace(/^customFields\./, '')
            const next = this.coerceParsedValue(targetKey, value)
            // Compared by value: a multi-select target holds an array, and a fresh array is
            // never === the previous one, so the watcher would re-fire forever.
            if (next !== undefined && !isEqual(this.formModel[targetKey], next)) this.formModel[targetKey] = next
          })
        } catch (error) {
          // A rule that fails to compile throws here. This runs on every keystroke, so one
          // bad setting must not take the form down with it, nor stop the other fields.
        }
      })
  }

  /** A parsed value is always a string; a date target needs it in the stored format. */
  coerceParsedValue(targetKey: string, value: string | string[]): unknown {
    const fieldType = this.selectedFormSchema[targetKey]?.fieldType
    // A multi-select holds a list of selection keys. Flattening it would drop every
    // key but the first and hand a string to the request builder, which maps over it.
    if (fieldType === ECustomFieldType.MULTI_SELECT) return Array.isArray(value) ? value : [value]

    const raw = Array.isArray(value) ? value[0] : value
    if (fieldType !== ECustomFieldType.DATE) return raw

    const formats = ['yyyy/MM/dd', 'MM/dd/yyyy', 'MMMM d, yyyy', 'yyyy-MM-dd', 'MM-dd-yyyy', 'yyyyMMdd', 'MMddyyyy']
    for (const inputFormat of formats) {
      const parsed = parse(raw, inputFormat, new Date())
      if (isValid(parsed)) return format(parsed, 'yyyy-MM-dd')
    }
    // Leave a date target alone rather than storing something unparseable.
    return undefined
  }

  get generateCustomDataForRequest(): Record<string, unknown> | ICustomFormFields {
    const copy: Record<string, unknown> = this.applyFieldComposers({ ...this.formModel })
    Object.entries(copy).forEach(([key, value]) => {
      if (value instanceof Date) {
        copy[key] = format(value, DATE_TIME.WITHOUT_SECOND)
      }
      // if value is Object and have default value
      if (value instanceof Object) {
        copy[key] = (copy[key] as IFormInputSchema)?.default || copy[key]
      }
    })
    const data = Object.keys(copy).reduce((acc, curr) => {
      if (this.selectedFormSchema[curr]) {
        const fieldType = this.selectedFormSchema[curr].fieldType
        if (fieldType === ECustomFieldType.TEXT) {
          return { ...acc, [curr]: { fieldType, text: copy[curr] } }
        }
        if (fieldType === ECustomFieldType.PHONE_NUMBER) {
          return {
            ...acc,
            [curr]: {
              fieldType,
              phoneNumber: {
                countryCode: this.selectedFormSchema[curr].countryCode,
                number: (copy[curr] as string).replaceAll('-', ''),
              },
            },
          }
        }
        if (fieldType === ECustomFieldType.EMAIL && !isEmpty(copy[curr])) {
          return { ...acc, [curr]: { fieldType, email: copy[curr] } }
        }
        if (fieldType === ECustomFieldType.NUMBER) {
          return { ...acc, [curr]: { fieldType, number: copy[curr] } }
        }
        if (fieldType === ECustomFieldType.ADJUSTMENT) {
          return { ...acc, [curr]: { fieldType, adjustment: copy[curr] } }
        }
        if (fieldType === ECustomFieldType.SINGLE_SELECT && !isEmpty(copy[curr])) {
          const selections = (this.selectedFormSchema?.[curr]?.selections as unknown) as Record<string, TOptions>
          const singleSelect = {
            key: copy[curr],
            value: selections[copy[curr] as string].label,
          }
          return { ...acc, [curr]: { fieldType, singleSelect } }
        }
        if (fieldType === ECustomFieldType.MULTI_SELECT && !isEmpty(copy[curr])) {
          const selections = (this.selectedFormSchema?.[curr]?.selections as unknown) as Record<string, TOptions>

          const multiSelect = (copy[curr] as Array<unknown>)
            .filter((k) => k !== '')
            .map((val) => ({
              key: val,
              value: selections[val as string].label,
            }))

          return { ...acc, [curr]: { fieldType, multiSelect } }
        }
        if (fieldType === ECustomFieldType.DATE) {
          const date = copy[curr]
          const field = { fieldType, date }
          if (!date) return { ...acc }
          return { ...acc, [curr]: field }
        }
        if (fieldType === ECustomFieldType.DATE_TIME_DATA) {
          const date = copy[curr]
          const field = { fieldType, dateTimeData: date }
          if (!date) return { ...acc }
          return { ...acc, [curr]: field }
        }
        if (fieldType === ECustomFieldType.LIST_STRING) {
          const value = copy[curr]
          const field = { fieldType, listString: value }
          if (!value) return { ...acc }
          return { ...acc, [curr]: field }
        }
        if (fieldType === ECustomFieldType.LIST_OBJECT) {
          const value = copy[curr]
          if (!value) return { ...acc }
          // CR-01: sub-field composers without an auto part are applied exactly ONCE,
          // here at request-build time — never inside InputListObject, whose localItems
          // must keep raw values so re-editing an element cannot compose twice.
          const field = { fieldType, listObject: this.composeListObjectRows(curr, value as IListObjectItem[]) }
          return { ...acc, [curr]: field }
        }
        if (fieldType === ECustomFieldType.ESIGN) {
          const eSign = copy[curr]
          if (!eSign) return { ...acc }
          return { ...acc, [curr]: { fieldType, eSign } }
        }
        if (fieldType === ECustomFieldType.FILES) {
          const files = (copy[curr] as Array<unknown>).map((val) => ({ path: val }))
          return { ...acc, [curr]: { fieldType, files } }
        }
        if (fieldType === ECustomFieldType.LOCATION) {
          return { ...acc, [curr]: copy[curr] }
        }
        if (fieldType === ECustomFieldType.REFERENCE) {
          const reference = copy[curr] as IFormInputSchema
          return {
            ...acc,
            [curr]: {
              fieldType,
              reference: {
                template: reference.template,
              },
            },
          }
        }

        if (fieldType === ECustomFieldType.GHG_EMISSION) {
          const form = copy[curr] as GhgEmissionInput
          const calculationType = this.selectedFormSchema[curr].calculationType
          const material = {
            calculationType: calculationType,
            gasTypes: ['CO_2'], // for now, we use only CO_2
            transportType: form.transportType,
            weight: form.weight,
            distance: form.distance,
            maxWeightCapacity: form.maxWeightCapacity,
            loadRate: form.loadRate,
            vehicleUsageType: form.vehicleUsageType,
            fuelConsumption: form.fuelConsumption,
          } as GhgEmissionCalculationMaterial

          const emissionMap = getGhgEmissionMap(material)
          const ghgEmission = {
            fieldType: fieldType,
            [ECustomFieldType.GHG_EMISSION]: emissionMap.get('CO_2'), // for now, we use only CO_2 value
            material: material,
          } as GhgEmissionCustomFieldConclusion
          return {
            ...acc,
            [curr]: ghgEmission,
          }
        }
      }
      return acc
    }, {})
    return data
  }

  convertProcessedDataToOriginal(processedData: Record<string, unknown>): Record<string, unknown> | ICustomFormFields {
    const originalData: Record<string, unknown> = {}
    Object.entries(processedData).forEach(([key, value]) => {
      if (value && typeof value === 'object' && 'fieldType' in value) {
        const fieldType = (value as IFormInputSchema).fieldType
        switch (fieldType) {
          case ECustomFieldType.SINGLE_SELECT:
            originalData[key] = ((value as unknown) as IFormInputSchema)?.singleSelect?.key
            break
          case ECustomFieldType.MULTI_SELECT:
            originalData[key] = ((value as unknown) as IFormInputSchema)?.multiSelect?.map((r) => r.key)
            break
          case ECustomFieldType.FILES:
            originalData[key] = (value as IFormInputSchema).files || []
            break
          case ECustomFieldType.LOCATION:
            originalData[key] = value
            break
          case ECustomFieldType.PHONE_NUMBER:
            originalData[key] = ((value as unknown) as IFormInputSchema)?.phoneNumber?.number
            break
          case ECustomFieldType.LIST_STRING:
          case ECustomFieldType.LIST_OBJECT:
            // the inputs bind to the bare array, not the { fieldType, listX } wrapper
            originalData[key] = (value as Record<string, unknown>)[fieldType] || []
            break
          case ECustomFieldType.TEXT:
          case ECustomFieldType.EMAIL:
          case ECustomFieldType.NUMBER:
          case ECustomFieldType.ADJUSTMENT:
          case ECustomFieldType.DATE:
          case ECustomFieldType.DATE_TIME_DATA:
          case ECustomFieldType.ESIGN:
            originalData[key] = (value as Record<string, unknown>)[fieldType]
            break
          default:
            originalData[key] = value
        }
      } else {
        originalData[key] = value
      }
    })
    return originalData
  }

  /**
   * Generate default form model for Form Schema
   */
  generateCustomFormModel(initData?: IActivationData, isTrackingDraft = false) {
    const textFieldtypes = new Set([
      ECustomFieldType.DATE,
      ECustomFieldType.DATE_TIME_DATA,
      ECustomFieldType.TEXT,
      ECustomFieldType.EMAIL,
    ])
    Object.entries(this.selectedFormSchema).forEach(async ([key, settings]) => {
      let result
      if (initData) {
        deepSearch(initData?.customFields || {}, key, (k: string, v: string | number | string[]) => (result = v))
      }

      // if copyFromPairedBarcode is true getting from API and set default value
      if (this.selectedFormSchema[key]?.copyFromPairedBarcode) {
        const res = await getChildrenValue(this.$route.params.barcodeId, key).catch(() => false)
        if (res) {
          this.selectedFormSchema[key].default = res
        }
      }
      const fieldType = settings?.fieldType || ''
      if (fieldType === ECustomFieldType.MULTI_SELECT) {
        const value: Array<string> = []
        if (result) {
          const multipleSelect = (result as IFormInputSchema).multiSelect
          if (multipleSelect) {
            multipleSelect.forEach((val) => value.push(val.key))
          }
        } else {
          Object.entries(settings.selections as TOptions).forEach(([optionValue, optionSetting]) => {
            if (optionSetting?.default) {
              value.push(optionValue)
            }
          })
        }
        this.formModel[key] = value
      }
      if (fieldType === ECustomFieldType.SINGLE_SELECT) {
        const selectedOption = Object.entries(settings.selections as TOptions).find(
          (radioSetting) => radioSetting[1]?.default
        )
        this.formModel[key] = ((result as unknown) as IFormInputSchema)?.singleSelect?.key || selectedOption?.[0]
      }
      if (fieldType === ECustomFieldType.NUMBER) {
        if (result) {
          if (typeof result !== 'object') {
            this.formModel[key] = result
          }
          if (typeof result === 'object') {
            this.formModel[key] = (result as IFormInputSchema)?.number || this.selectedFormSchema[key]?.default
          }
        }
      }
      if (fieldType === ECustomFieldType.ADJUSTMENT) {
        if (result) {
          if (typeof result !== 'object') {
            this.formModel[key] = result
          }
          if (typeof result === 'object') {
            this.formModel[key] = (result as IFormInputSchema)?.adjustment || this.selectedFormSchema[key]?.default
          }
        }
      }
      if (fieldType === ECustomFieldType.ESIGN) {
        this.formModel[key] = result?.[ECustomFieldType.ESIGN]
      }
      if (fieldType === ECustomFieldType.REFERENCE) {
        this.formModel[key] = cloneDeep(result?.[ECustomFieldType.REFERENCE]) || settings
      }

      if (fieldType === ECustomFieldType.LIST_STRING) {
        this.formModel[key] = cloneDeep(result?.[ECustomFieldType.LIST_STRING]) || []
      }

      if (fieldType === ECustomFieldType.LIST_OBJECT) {
        this.formModel[key] = cloneDeep(result?.[ECustomFieldType.LIST_OBJECT]) || []
      }

      if (fieldType && textFieldtypes.has(fieldType as ECustomFieldType)) {
        const type = typeof result
        if (result) {
          if (type === 'object') {
            this.formModel[key] =
              (result as IFormInputSchema).text ||
              (result as IFormInputSchema).date ||
              (result as IFormInputSchema).email ||
              this.selectedFormSchema[key]?.default ||
              ''
          }
        }
      }

      if (fieldType === ECustomFieldType.PHONE_NUMBER) {
        if (result && (result as IFormInputSchema).phoneNumber) {
          const r = result as IFormInputSchema
          this.formModel[key] = maskPhoneNum(r.phoneNumber?.number ?? '', r.phoneNumber?.countryCode ?? 'JP')
        }
      }
      if (fieldType === ECustomFieldType.FILES) {
        if (result) {
          this.formModel[key] = (result as IFormInputSchema).files
        }
      }
    })

    if (isTrackingDraft && this.keyTrackingDataDraft) {
      const draft = localStorage.getItem(this.keyTrackingDataDraft)
      if (draft) {
        try {
          const parsedDraft = JSON.parse(draft)
          if (parsedDraft && typeof parsedDraft === 'object') {
            this.formModel = parsedDraft
          }
        } catch (error) {
          localStorage.removeItem(this.keyTrackingDataDraft)
        }
      }
    }
  }

  async saveOCRImage(event: Record<string, string>) {
    try {
      this.loading = true
      const flattenFields = cloneDeep(this.flattenFields) || {}
      const basicActivationField: Record<string, string> = {
        trackingNumber: this.isAutoSaveOcrImageTrackingNumber || '',
        externalId: this.isAutoSaveOcrImageExternalId || '',
      }
      const keys = Object.keys(event)
      for (const index in keys) {
        const key = keys[index]
        if (isEmpty(key)) return
        const ocrImageAutoSaveFieldKey =
          flattenFields?.[key]?.ocrImageAutoSaveField || basicActivationField?.[key] || ''

        if (ocrImageAutoSaveFieldKey) {
          const previousImages: string[] = this.temporaryFieldsOCRImages?.[key]?.[ocrImageAutoSaveFieldKey] ?? []
          if (previousImages.length) {
            const withoutPrevious = (list: unknown) =>
              (Array.isArray(list) ? list : []).filter((el) => !previousImages.includes(el as string))

            if (this.imagesFromOCR?.[ocrImageAutoSaveFieldKey]) {
              this.imagesFromOCR[ocrImageAutoSaveFieldKey] = withoutPrevious(
                this.imagesFromOCR[ocrImageAutoSaveFieldKey]
              )
            }
            if (Array.isArray(this.formModel?.[ocrImageAutoSaveFieldKey])) {
              this.formModel[ocrImageAutoSaveFieldKey] = withoutPrevious(this.formModel[ocrImageAutoSaveFieldKey])
            }
            this.temporaryFieldsOCRImages[key][ocrImageAutoSaveFieldKey] = []
          }

          const file = dataURLtoFile(event[key])
          const formData = new FormData()
          if (!file) return
          formData.append('barcodeId', this.barcode?.id)
          const extension = this.getFileExtension(file)
          formData.append('file', file, `OCR_${ocrImageAutoSaveFieldKey}.${extension}`)
          const image = await uploadImage(formData).catch((e) => errorHandler(e))
          event[key] = image
          if (this.imagesFromOCR?.[ocrImageAutoSaveFieldKey]) {
            // eslint-disable-next-line no-unused-expressions
            this.imagesFromOCR?.[ocrImageAutoSaveFieldKey].push(image)
          } else {
            this.imagesFromOCR = { ...this.imagesFromOCR, ...{ [ocrImageAutoSaveFieldKey]: [image] } }
          }

          // Save images to temporary fields to compare delete images when re-ocr of fields ↑
          if (this.temporaryFieldsOCRImages?.[key]?.[ocrImageAutoSaveFieldKey]) {
            // eslint-disable-next-line no-unused-expressions
            this.temporaryFieldsOCRImages?.[key]?.[ocrImageAutoSaveFieldKey]?.push(image)
          } else {
            this.temporaryFieldsOCRImages = {
              ...this.temporaryFieldsOCRImages,
              ...{
                [key]: {
                  [ocrImageAutoSaveFieldKey]: [image],
                },
              },
            }
          }

          const customFieldOcrImages = { [ocrImageAutoSaveFieldKey]: [file] }
          this.$store.dispatch(UPDATE_CUSTOM_FIELD_OCR_IMAGE_RESULT, customFieldOcrImages)

          URL.revokeObjectURL(event[key])
        }
      }
    } catch (error) {
    } finally {
      this.loading = false
    }
  }

  getFileExtension(file: Blob) {
    if (!file) return
    const splitedType = file.type.split('/')
    return splitedType[splitedType.length - 1]
  }
}
